import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Logo from '../Logo';

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);

  // Check for invite/recovery tokens in URL on mount
  useEffect(() => {
    const checkForAuthCallback = async () => {
      // Check URL hash for tokens (Supabase puts them there after redirect)
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        setIsLoading(true);
        try {
          // Let Supabase handle the token exchange
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) throw error;

          if (session?.user) {
            // User is authenticated via invite link
            setInviteEmail(session.user.email || null);
            setIsSettingPassword(true);
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (err: any) {
          console.error('Auth callback error:', err);
          setError('Invalid or expired invitation link. Please request a new invite.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    checkForAuthCallback();
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      setResetMessage('Password set successfully! You can now access the dashboard.');
      setTimeout(() => {
        navigate('/manage');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if user is in admins table (done by AdminRoute, but good UX to check early)
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('id')
        .eq('email', email.toLowerCase())
        .maybeSingle();

      if (adminError || !adminData) {
        await supabase.auth.signOut();
        setError('Access Denied: You are not authorized to view this portal.');
        return;
      }

      navigate('/manage');
    } catch (err: any) {
      setError(err.message || 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address first.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResetMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) throw error;
      setResetMessage("Check your email for the password reset link!");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-fade-in">
      <button
        onClick={() => navigate('/')}
        className="mb-8 flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
        Return to Public Site
      </button>

      <div className="bg-slate-800 p-8 lg:p-10 rounded-[2.5rem] shadow-2xl border border-slate-700">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-2">
            {isSettingPassword ? 'Set Your Password' : isForgotPassword ? 'Reset Password' : 'Internal Portal'}
          </h2>
          <p className="text-slate-400">
            {isSettingPassword
              ? `Welcome${inviteEmail ? `, ${inviteEmail}` : ''}! Please set your password to continue.`
              : isForgotPassword
              ? 'Enter your admin email to receive a reset link.'
              : 'Restricted access for Queenli administrators only.'}
          </p>
        </div>

        {resetMessage && (
          <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-xl text-sm font-bold text-center">
            {resetMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-bold text-center">
            {error}
          </div>
        )}

        {/* Password Setup Form (for invited users) */}
        {isSettingPassword ? (
          <form onSubmit={handleSetPassword} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-bold text-slate-300">New Password</label>
              <input
                required
                type="password"
                placeholder="At least 8 characters"
                className="w-full px-5 py-4 bg-slate-900 border border-slate-700 text-white rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-slate-300">Confirm Password</label>
              <input
                required
                type="password"
                placeholder="Repeat password"
                className="w-full px-5 py-4 bg-slate-900 border border-slate-700 text-white rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-5 bg-purple-600 text-white rounded-full text-lg font-bold hover:bg-purple-500 transition-all shadow-lg active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : 'Set Password & Continue'}
            </button>
          </form>
        ) : (
          /* Login / Forgot Password Form */
          <form onSubmit={isForgotPassword ? handleForgotPassword : handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-bold text-slate-300">Admin Email</label>
              <input
                required
                type="email"
                placeholder="admin@torek.io"
                className="w-full px-5 py-4 bg-slate-900 border border-slate-700 text-white rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {!isForgotPassword && (
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-300">Password</label>
                <input
                  required
                  type="password"
                  placeholder="••••••••"
                  className="w-full px-5 py-4 bg-slate-900 border border-slate-700 text-white rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-5 bg-blue-600 text-white rounded-full text-lg font-bold hover:bg-blue-500 transition-all shadow-lg active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (isForgotPassword ? 'Send Reset Link' : 'Access Dashboard')}
            </button>

            {!isForgotPassword && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-slate-400 text-sm hover:text-white transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {isForgotPassword && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="text-slate-400 text-sm hover:text-white transition-colors"
                >
                  ← Back to Login
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLoginPage;
